/**
 * This controller handles all the business logic related to the LoginPage
 *
 */

import PermissionsHandler from "../../services/api/permissionsHandler";
import AccountHandler from "../../services/api/accountHandler";
import AgentHandler from "../../services/api/agentHandler";
import LocationHandler from "../../services/api/locationHandler";
import ReasonCodeHandler from "../../services/api/reasonCodeHandler";
import StatusCodeHandler from "../../services/api/statusCodeHandler";
import CallManagerHandler from "../../services/api/callManagerHandler";
import CustomValueHandler from "../../services/api/customValuesHandler";

/**
 * This function gets permissions from the WebAPI
 *
 */
export async function getPermissions() {
  return PermissionsHandler.getPermissions().then((response) => {
    return response;
  });
}

/**
 * This function must succeed to permit app access
 */
export const getReasonCodes = (permissionStore) => {
  if (!permissionStore.havePermissions("Reason Codes", ["GetAll"])) {
    return Promise.reject(new Error("No User Permissions for Reason Codes"));
  }
  // we have the permissions so fire the promise chain
  return ReasonCodeHandler.getAllReasonCodes();
};

/**
 * This function must succeed to permit app access
 */
export const getStatusCodes = (permissionStore) => {
  if (!permissionStore.havePermissions("Status Codes", ["GetStatus"])) {
    return Promise.reject(new Error("No User Permissions for Status Codes"));
  }
  // we have the permissions so fire the promise chain
  return StatusCodeHandler.getAllStatusCodes();
};

/**
 * This function must succeed to permit app access
 */
export const getCurrentAgentOverview = (permissionStore) => {
  if (!permissionStore.havePermissions("Agent", ["MyDetail"])) {
    return Promise.reject(new Error("No User Permissions for Current Agent Overview"));
  }
  // we have the permissions so fire the promise chain
  return AgentHandler.getCurrentAgentOverview();
};

/**
 * This function must succeed to permit app access
 */
export const getAccountAbout = (permissionStore) => {
  if (!permissionStore.havePermissions("Accounts", ["About"])) {
    return Promise.reject(new Error("No User Permissions for Account About"));
  }
  // we have the permissions so fire the promise chain
  return AccountHandler.About();
};

/**
 * This function can fail - all agents are not required
 */
export const getAllAgents = (permissionStore) => {
  if (!permissionStore.havePermissions("Call Manager", ["Monitor"])) {
    // resolve the promise as this is ok to fail
    return Promise.resolve([]);
  }
  // we have the permissions so fire the promise chain
  return CallManagerHandler.getAllAgents();
};

/**
 * This function can fail - locations are not required
 */
export const getLocations = (permissionStore) => {
  if (!permissionStore.havePermissions("Locations", ["GetAll"])) {
    // resolve the promise as this is ok to fail
    return Promise.resolve([]);
  }
  // we have the permissions so fire the promise chain
  return LocationHandler.getAllLocations();
};

/**
 * This function gets the data we need on launching the app
 */
export function initData(permissionStore) {
  return Promise.all([
    getReasonCodes(permissionStore),
    getStatusCodes(permissionStore),
    getLocations(permissionStore),
    getCurrentAgentOverview(permissionStore),
    getAccountAbout(permissionStore),
    getAllAgents(permissionStore),
    getPasswordHints(),
  ])
    .then((response) => {
      const resp = {
        reasonCodes: response[0],
        statusCodes: response[1],
        locations: response[2],
        currentAgent: response[3],
        about: response[4],
        agents: response[5],
        passwordHints: response[6],
      };
      return resp;
    })
    .catch((e) => {
      console.warn("Error initialising data...");
    });
}

export const getPasswordHints = () => {
  return AccountHandler.getPasswordHints()
    .then((response) => {
      return response;
    })
    .catch((err) => {
      console.warn("Error getting password hints...");
      throw (err);
    });
};

export const getAccountProfile = () => {
  return AccountHandler.getAccountProfile()
    .then((response) => {
      return response;
    })
    .catch((err) => {
      console.warn("Error getting account profile...");
      throw (err);
    });
}

export const getCustomValue = (key, permissionStore) => {

  if (!permissionStore.havePermissions("Custom Values", ["Get"])) {
    // resolve the promise as this is ok to fail
    return Promise.resolve(null);
  }

  // we have the permissions so fire the promise chain
  return CustomValueHandler.getCustomValue(key)
    .then((response) => {
      return response;
    })
    .catch((err) => {
      if (err.response && err.response.status === 404) {
        return null;
      }
      console.warn("Error getting custom value...");
      throw (err);
    });
}

export const setCustomValue = (key, value, permissionStore) => {
  if (!permissionStore.havePermissions("Custom Values", ["Set"])) {
    // resolve the promise as this is ok to fail
    return Promise.resolve(null);
  }

  return CustomValueHandler.setCustomValue(key, value)
    .then((response) => {
      return response;
    })
    .catch((err) => {
      console.warn("Error setting custom value...");
      throw (err);
    });
}
